import { useEffect, useMemo, useRef, useState } from "react";

import { useLazyGetBankQuery, useLazyGetBanksByServerQuery } from "../../redux/banks/bankAPI";
import GETJwtToken from "../../redux/authentication/authentication";
import {
  extractBankFloorOpenings,
  extractLiftDoorMaps,
  unwrapBankResponse,
  coerceFlatBufferText,
  type BankFloorOpening,
  type LiftDoorMap,
} from "../../utils/bankFloorOpenings";
import formatUuidT from "../../flatbuffers/display/uuid";
import { IdNameOption } from "./scheduledActionTypes";

/** What one `GET /banks/{id}` yields for the editor's target pickers. */
interface BankTargets {
  floors: BankFloorOpening[];
  lifts: IdNameOption[];
  /** Each lift's own per-floor doors, keyed by lift id. */
  liftDoors: Record<string, LiftDoorMap>;
}

/** Why a bank's targets could not be offered. Absent when it loaded fine. */
export type BankLoadFailure = "request-failed" | "no-floors";

export interface BankTargetOptions {
  floorsByBank: Record<string, BankFloorOpening[]>;
  liftsByBank: Record<string, IdNameOption[]>;
  /**
   * Per-lift, per-floor door configuration, keyed by bank then lift.
   *
   * The bank-level flags only say whether ANY car in the bank has an opening at
   * a floor. An action targeting particular cars needs the per-car answer, which
   * is what `lift_floors` carries.
   */
  liftDoorsByBank: Record<string, Record<string, LiftDoorMap>>;
  /**
   * True while at least one selected bank is still being fetched.
   *
   * Without it, `floorsByBank[id] === undefined` conflates "still loading" with
   * "this bank has no floors" — the two states the editor most needs to tell
   * apart. Reading the first as the second made the editor open in the expert
   * expression mode and then swap to the grid when the response landed, pulling
   * the input out from under anyone who had started typing into it.
   */
  isLoading: boolean;
  /**
   * Why each bank that did not load failed, keyed by bank id.
   *
   * The editor previously collapsed every cause into one sentence — "the floor
   * list could not be loaded" — which is what a 403 on a bank the operator
   * cannot see, a dropped connection, and a bank with genuinely no floor rows
   * all looked like. Distinguishing them is the difference between a message an
   * operator can act on and one that only says "something went wrong".
   */
  failures: Record<string, BankLoadFailure>;
}

/**
 * Pull the bank's cars off the same response the floors came from. Accepts the
 * camelCase and snake_case shapes for the same reason `extractBankFloorOpenings`
 * does — the bank row reaches the UI from more than one serialiser.
 */
function extractBankLifts(bankData: unknown): IdNameOption[] {
  const bank = unwrapBankResponse(bankData);
  const raw =
    typeof bank === "object" && bank !== null ? (bank as Record<string, unknown>).lifts : undefined;
  if (!Array.isArray(raw)) return [];

  return raw
    .map((lift): IdNameOption | null => {
      if (typeof lift !== "object" || lift === null) return null;
      const row = lift as Record<string, unknown>;
      // `id` is a UuidT on the FlatBuffer path; String() on one yields
      // "[object Object]" for every car, which is non-empty and so would pass
      // any truthiness guard while making all of them the same option.
      const id = formatUuidT((row.id ?? row.ID) as Parameters<typeof formatUuidT>[0]);
      if (id === "") return null;
      const name = coerceFlatBufferText(row.name ?? row.Name);

      return { id, name: name ?? id };
    })
    .filter((lift): lift is IdNameOption => lift !== null);
}

/**
 * Why a bank could not be offered, or `undefined` when it loaded fine.
 *
 * A failed REQUEST and a bank that answered with no floor rows are different
 * problems with different fixes — the second is corrected in Lifts & Floors, the
 * first is a permission or connectivity matter — and collapsing them into one
 * message told an operator neither.
 */
function classifyFailure(targets: BankTargets | null): BankLoadFailure | undefined {
  if (targets === null) return "request-failed";
  if (targets.floors.length === 0) return "no-floors";

  return undefined;
}

/**
 * Load each bank's cars, floors and front/rear openings, keyed by bank id.
 *
 * The scheduled-action editor renders the floors as a per-floor checkbox grid
 * and validates typed floor ranges against them. `GET /banks/{id}` is the only
 * endpoint that returns the nested `bankFloors` with their door flags — the
 * light and list endpoints do not — so one request per bank is unavoidable, and
 * the cars come back on the same response for free.
 *
 * Each bank is recorded THE MOMENT ITS OWN RESPONSE LANDS, independently of the
 * others. An earlier version awaited the whole batch and dropped all of it if the
 * bank selection changed while any request was in flight — and because the
 * already-requested guard is never cleared, the dropped bank could not be asked
 * for again for the life of the mount. Adding a second bank while the first was
 * loading therefore lost the first one permanently: its floors vanished from the
 * validation domain, and any stored range naming one of them forced the editor
 * into Advanced mode under "this floor range cannot be shown as a list".
 *
 * A bank that fails is REMOVED from the guard so a later selection change can
 * retry it. It stays absent from the maps meanwhile, which the editor reads as
 * "floors unknown" and answers by falling back to the Advanced expression rather
 * than showing an empty grid that looks like a bank with no floors.
 */
export function useBankFloorOpenings(bankIds: string[]): BankTargetOptions {
  const [triggerGetBank] = useLazyGetBankQuery();
  const [loaded, setLoaded] = useState<Record<string, BankTargets>>({});
  const [pending, setPending] = useState<string[]>([]);
  const [failures, setFailures] = useState<Record<string, BankLoadFailure>>({});

  // Ids already requested. A ref rather than state so recording one does not
  // re-run the effect that records it.
  const requested = useRef(new Set<string>());

  // Tracks the MOUNT, not the effect run. The previous code used the effect's
  // own cleanup as the "give up" signal, which conflated "the bank list changed"
  // with "this component is gone" — and only the second is a reason to discard a
  // response that is already paid for.
  const mounted = useRef(true);
  useEffect(
    () => () => {
      mounted.current = false;
    },
    [],
  );

  // Join the ids so the effect keys off the CONTENT of the array, not its
  // identity. Callers build this list with map()/filter() inside a render, so a
  // new array arrives every time and an identity-keyed effect would re-run on
  // every keystroke in the editor.
  const key = useMemo(() => bankIds.filter((id) => id !== "").join("|"), [bankIds]);

  useEffect(() => {
    const ids = key === "" ? [] : key.split("|");
    const missing = ids.filter((id) => !requested.current.has(id));
    if (missing.length === 0) return;

    missing.forEach((id) => requested.current.add(id));
    setPending((previous) => [...previous, ...missing]);

    const settle = (bankId: string, targets: BankTargets | null): void => {
      if (!mounted.current) return;

      // Record WHY, so the editor can say something an operator can act on
      // rather than one sentence covering every cause.
      const failure = classifyFailure(targets);

      setFailures((previous) => {
        if (failure === undefined) {
          if (previous[bankId] === undefined) return previous;
          const next = { ...previous };
          delete next[bankId];

          return next;
        }

        return previous[bankId] === failure ? previous : { ...previous, [bankId]: failure };
      });

      if (targets !== null && (targets.floors.length > 0 || targets.lifts.length > 0)) {
        // An empty result is NOT recorded: leaving the bank out is what tells the
        // editor the floors are unknown. Recording an empty entry would instead
        // assert "this bank has no floors", and the grid would render an empty
        // list an operator could neither use nor explain.
        setLoaded((previous) => ({ ...previous, [bankId]: targets }));
      }

      setPending((previous) => previous.filter((id) => id !== bankId));
    };

    const load = async (): Promise<void> => {
      const token = await GETJwtToken();

      await Promise.all(
        missing.map(async (bankId) => {
          try {
            const result = await triggerGetBank({ bankId, token }).unwrap();

            // The WHOLE response, not `result.data`. On the FlatBuffer path —
            // which is every config_wizard build, because the base query sends
            // `Accept: application/flatbuffers` — the base query returns the
            // unpacked Bank itself, so `.data` is undefined and every bank
            // resolved to zero floors. Both extractors strip a JSON envelope
            // themselves, so they take the response as it arrives.
            settle(bankId, {
              floors: extractBankFloorOpenings(result),
              lifts: extractBankLifts(result),
              liftDoors: extractLiftDoorMaps(result),
            });
          } catch {
            // Forget the id so a later selection change can try again. Keeping it
            // would make one transient failure permanent for the whole session.
            requested.current.delete(bankId);
            settle(bankId, null);
          }
        }),
      );
    };

    // An effect may not return a promise, and a rejection here would only come
    // from the token call; each per-bank fetch already catches its own.
    load().catch(() => {
      missing.forEach((bankId) => {
        requested.current.delete(bankId);
        settle(bankId, null);
      });
    });
  }, [key, triggerGetBank]);

  return useMemo(() => {
    const floorsByBank: Record<string, BankFloorOpening[]> = {};
    const liftsByBank: Record<string, IdNameOption[]> = {};
    const liftDoorsByBank: Record<string, Record<string, LiftDoorMap>> = {};

    Object.entries(loaded).forEach(([bankId, targets]) => {
      if (targets.floors.length > 0) floorsByBank[bankId] = targets.floors;
      if (targets.lifts.length > 0) liftsByBank[bankId] = targets.lifts;
      if (Object.keys(targets.liftDoors).length > 0) liftDoorsByBank[bankId] = targets.liftDoors;
    });

    return {
      floorsByBank,
      liftsByBank,
      liftDoorsByBank,
      isLoading: pending.length > 0,
      failures,
    };
  }, [loaded, pending, failures]);
}

export default useBankFloorOpenings;

/**
 * The same targets, sourced from the server's bank LIST rather than one request
 * per bank.
 *
 * `GET /banks/{id}` is materially more restrictive than `GET /banks/byServer/{id}`
 * on the deployed backend: the per-bank handler authorises against the bank's
 * resolved org AND runs `enforceBankScope(bank, PermissionActionView)`, while the
 * list handler authorises with `uuid.Nil` and applies no per-bank scope gate. So
 * an operator who can see a bank everywhere else in the wizard could still be
 * refused the per-bank read, and the floor grid would report that it could not
 * load the floors — for a bank plainly visible on the neighbouring step.
 *
 * The list carries exactly the same relations (`BankFloors`, `Lifts.LiftFloors`,
 * preloaded in `FindBanksByServer`), so nothing is lost, and it is ONE request
 * instead of one per bank. It is also the endpoint the Lifts & Floors step
 * already uses, which is why that screen renders floors for banks this hook
 * could not read.
 *
 * Requires a server id, which only the wizard has. The bank control panel keeps
 * the per-bank hook: it is scoped to a single bank the operator is already
 * viewing, where the scope gate is the intended behaviour rather than an
 * obstacle.
 */
export function useServerBankTargets(serverId: string, bankIds: string[]): BankTargetOptions {
  const [triggerGetBanksByServer] = useLazyGetBanksByServerQuery();
  const [targets, setTargets] = useState<Record<string, BankTargets>>({});
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  const mounted = useRef(true);
  useEffect(
    () => () => {
      mounted.current = false;
    },
    [],
  );

  // Keyed on CONTENT so a caller rebuilding the array each render does not
  // re-request, matching the per-bank hook.
  const bankKey = useMemo(() => bankIds.filter((id) => id !== "").join("|"), [bankIds]);

  // The server already asked for. RTK Query's trigger is a stable reference, so
  // in practice the effect keys off `serverId` alone — but it is IN the dep list,
  // and a trigger that ever changed identity would otherwise refetch the whole
  // bank list on a re-render. The per-bank hook guards the same way.
  const requestedServer = useRef<string | null>(null);

  useEffect(() => {
    if (serverId === "") return undefined;
    if (requestedServer.current === serverId) return undefined;
    requestedServer.current = serverId;

    let cancelled = false;
    setLoading(true);

    const load = async (): Promise<void> => {
      const token = await GETJwtToken();

      try {
        const response = await triggerGetBanksByServer({
          idServer: serverId,
          page: 1,
          limit: 9999,
          searchAndFilterString: "",
          token,
        }).unwrap();

        // `.data` on BOTH paths: the JSON envelope keys the array there, and the
        // FlatBuffer path decodes to a PaginatedBanks whose `data` is the bank
        // list. Each ELEMENT is the bank, which is what the extractors take —
        // handing them the paginated wrapper would find no floors on it.
        const banks = readBankList(response);
        if (cancelled || !mounted.current) return;

        const next: Record<string, BankTargets> = {};
        banks.forEach((bank) => {
          const bankId = formatUuidT(
            (readBankField(bank, "id") ?? readBankField(bank, "ID")) as Parameters<
              typeof formatUuidT
            >[0],
          );
          if (bankId === "") return;

          next[bankId] = {
            floors: extractBankFloorOpenings(bank),
            lifts: extractBankLifts(bank),
            liftDoors: extractLiftDoorMaps(bank),
          };
        });

        setTargets(next);
        setFailed(false);
      } catch {
        if (!cancelled && mounted.current) setFailed(true);
      } finally {
        if (!cancelled && mounted.current) setLoading(false);
      }
    };

    load().catch(() => {
      if (!cancelled && mounted.current) {
        setFailed(true);
        setLoading(false);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [serverId, triggerGetBanksByServer]);

  return useMemo(() => {
    const floorsByBank: Record<string, BankFloorOpening[]> = {};
    const liftsByBank: Record<string, IdNameOption[]> = {};
    const liftDoorsByBank: Record<string, Record<string, LiftDoorMap>> = {};
    const failures: Record<string, BankLoadFailure> = {};

    // One request covers every bank, so when it fails they ALL failed — recorded
    // per bank so the editor can name the reason for whichever ones are selected.
    if (failed) {
      (bankKey === "" ? [] : bankKey.split("|")).forEach((bankId) => {
        failures[bankId] = "request-failed";
      });
    }

    Object.entries(targets).forEach(([bankId, bank]) => {
      if (bank.floors.length > 0) floorsByBank[bankId] = bank.floors;
      else failures[bankId] = "no-floors";
      if (bank.lifts.length > 0) liftsByBank[bankId] = bank.lifts;
      if (Object.keys(bank.liftDoors).length > 0) liftDoorsByBank[bankId] = bank.liftDoors;
    });

    return { floorsByBank, liftsByBank, liftDoorsByBank, isLoading: loading, failures };
  }, [targets, loading, failed, bankKey]);
}

/** The bank array out of either response shape. */
function readBankList(response: unknown): unknown[] {
  const data = readBankField(response, "data");

  return Array.isArray(data) ? data : [];
}

/** One property off a value that may not be an object. */
function readBankField(source: unknown, key: string): unknown {
  if (typeof source !== "object" || source === null) return undefined;

  return (source as Record<string, unknown>)[key];
}
